import { useEffect, useRef, useCallback } from 'react';

interface ResumePromptModalProps {
  isOpen: boolean;
  /** The question the user would resume at (savedPosition + 1), for the message copy. */
  resumeQuestionNumber: number;
  /** Total problems in the current set, for the message copy. */
  totalQuestions: number;
  /** Human-readable feature name, e.g. "Important Problem Sequences". */
  featureLabel: string;
  onContinue: () => void;
  onRestart: () => void;
}

/**
 * Feature 026 — the continue-or-restart prompt shown when a user re-enters Important Problem
 * Sequences or Review Problems with unfinished saved progress.
 *
 * DELIBERATELY non-dismissible (spec clarification 2026-07-24): entering the session requires an
 * explicit choice, so there is no Esc handler and no backdrop-click dismissal — a justified
 * deviation from the shared `useEscapeClose` modal rule (Constitution VI), documented in
 * specs/026-resume-problem-sessions/plan.md. Focus is trapped between the two buttons and the
 * primary action is auto-focused, so it stays fully keyboard-operable.
 */
export function ResumePromptModal({
  isOpen,
  resumeQuestionNumber,
  totalQuestions,
  featureLabel,
  onContinue,
  onRestart,
}: ResumePromptModalProps) {
  const continueButtonRef = useRef<HTMLButtonElement>(null);
  const restartButtonRef = useRef<HTMLButtonElement>(null);

  // Auto-focus the primary action when the prompt opens.
  useEffect(() => {
    if (isOpen) continueButtonRef.current?.focus();
  }, [isOpen]);

  // Prevent body scroll while the prompt is open.
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus trap: keep Tab / Shift+Tab cycling between the two buttons. Esc is intentionally NOT
  // handled — the prompt is non-dismissible.
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const first = continueButtonRef.current;
    const last = restartButtonRef.current;
    if (!first || !last) return;

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="resume-prompt-title"
      aria-describedby="resume-prompt-message"
      data-testid="resume-prompt"
      onKeyDown={handleKeyDown}
    >
      {/* Backdrop — non-interactive: clicking it does NOT dismiss the prompt. */}
      <div className="absolute inset-0 bg-black bg-opacity-50" aria-hidden="true" />

      <div className="relative bg-card rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h2 id="resume-prompt-title" className="text-lg font-semibold text-ink mb-2">
          Continue where you left off?
        </h2>
        <p id="resume-prompt-message" data-testid="resume-prompt-message" className="text-ink-soft mb-6">
          You have unfinished progress in {featureLabel}. Continue from question{' '}
          {resumeQuestionNumber} of {totalQuestions}, or start again from the beginning?
        </p>

        <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
          <button
            ref={restartButtonRef}
            type="button"
            onClick={onRestart}
            data-testid="resume-prompt-restart"
            className="rounded-md border border-brass bg-cream px-4 py-2 font-medium text-ink hover:bg-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-brass"
          >
            Start from the beginning
          </button>
          <button
            ref={continueButtonRef}
            type="button"
            onClick={onContinue}
            data-testid="resume-prompt-continue"
            className="rounded-md bg-primary-600 px-4 py-2 font-medium text-white hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
          >
            Continue from where you left off
          </button>
        </div>
      </div>
    </div>
  );
}
